/**
 * @fileoverview `/<author>/<slug>` -- the readable URL, resolved and rendered.
 *
 * IT RENDERS IN PLACE AND DOES NOT REDIRECT. Swapping the address bar for the
 * `naddr` would hand the reader back the unreadable form the moment they
 * arrived, which is the opposite of what the readable form is for -- it is the
 * one we teach people to share, because it survives a page changing kind.
 *
 * It resolves to the canonical address first and then hands off to the same
 * component the canonical route uses. Not a second reading path: everything
 * about fetching, hash-verifying and sanitising a page stays in one place, and
 * this route's whole job is turning two segments into an address.
 *
 * THE LOADING MARKUP IS THE SAME MARKUP the page route shows, because resolving
 * a name and loading a page are one wait as far as the reader is concerned and
 * two spinners of different shapes would be a worse answer than one.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PageRoute } from './PageRoute';

import { resolveReadableAddress } from '@/services/pages/resolveReadable';

export function ReadableRoute() {
  const { author = '', slug = '' } = useParams();
  const [naddr, setNaddr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setNaddr(null);
    // `void`: deliberately not awaited, and resolveReadableAddress never
    // rejects -- a name nobody has registered produces an address that names
    // nothing, so there is no error branch for this component to render.
    void resolveReadableAddress(author, slug).then((address) => {
      if (live) setNaddr(address);
    });
    return () => {
      live = false;
    };
  }, [author, slug]);

  if (naddr === null) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  // `showCanonical` is true only here. A reader who only ever sees readable
  // links only ever shares readable links, and those stop working if our name
  // service does -- which is the single point of failure the canonical form
  // exists to avoid. So the page reached by a name offers the permanent one.
  return <PageRoute naddr={naddr} showCanonical />;
}
